import React from 'react';
import { useIntl } from 'react-intl';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';

import { COADisplayAccount } from '../../types';
import { useCoaApiView } from '../../hooks/useCoaApiView';
import { useRouteIds } from '../../../hooks/useRouteIds';

interface DeleteGroupDialogProps {
  coaRow: COADisplayAccount;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteGroupDialog = ({ coaRow, isOpen, onClose }: DeleteGroupDialogProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId } = useRouteIds();

  const { isDeletingGroup, errorDeletingGroup, deleteCoaGroup } = useCoaApiView(workspaceId, organisationId);

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: isDeletingGroup,
    error: errorDeletingGroup,
    onAction: () => deleteCoaGroup(coaRow.id),
    onClose
  });

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleAction}
      screen={screen}
      title={intl.formatMessage({ id: 'predict.delete_group', defaultMessage: 'Delete group' })}
      subtitle={coaRow.name}
      thingsToKnow={[
        intl.formatMessage({
          id: 'predict.coa.delete.things_to_know.accounts_added_back',
          defaultMessage: 'All accounts within the group will be added back to the accounts list'
        }),
        intl.formatMessage({
          id: 'predict.coa.delete.things_to_know.users_lose_access',
          defaultMessage: 'All users will immediately lose access to this group'
        })
      ]}
      deleteDescription={intl.formatMessage({
        id: 'predict.coa.delete_group_description',
        defaultMessage: 'Are you sure you want to delete this group?'
      })}
      loadingMessage={intl.formatMessage({
        id: 'common.deleting_group',
        defaultMessage: 'Deleting group...'
      })}
      successMessage={intl.formatMessage({
        id: 'predict.coa.group_deleted',
        defaultMessage: 'Group deleted'
      })}
      errorMessage={errorDeletingGroup?.message}
      dataTestId="delete-group-dialog"
    />
  );
};
